import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useToast } from '../../components/Toast';
import { UpperInput } from '../../components/inputs/UpperInput';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { MenuAcoesLinha, type AcaoLinha } from '../../components/MenuAcoesLinha';
import { StatusBadge } from '../../components/StatusBadge';
import { Pencil, Ban } from 'lucide-react';

interface SitTrib {
  id: number;
  codigo: string;
  tipo: string;
  descricao: string;
  aliquota: string | null;
  status: string;
}

export function SituacoesTributarias() {
  const toast = useToast();
  const [itens, setItens] = useState<SitTrib[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [mostrarInativas, setMostrarInativas] = useState(false);
  const [editando, setEditando] = useState<SitTrib | null>(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ codigo: '', tipo: 'ICMS', descricao: '', aliquota: '' });
  const [confirmar, setConfirmar] = useState<SitTrib | null>(null);

  async function carregar() {
    setCarregando(true);
    try {
      const lista = await api.get<SitTrib[]>('/api/situacoes-tributarias', { query: { incluir_inativos: mostrarInativas } });
      setItens(lista);
    } catch (e: any) { toast.erro(e.message); }
    finally { setCarregando(false); }
  }
  useEffect(() => { void carregar(); /* eslint-disable-next-line */ }, [mostrarInativas]);

  const filtradas = itens.filter((s) =>
    s.codigo.toLowerCase().includes(busca.toLowerCase()) ||
    s.descricao.toLowerCase().includes(busca.toLowerCase()),
  );

  function abrirNovo() {
    setEditando(null);
    setForm({ codigo: '', tipo: 'ICMS', descricao: '', aliquota: '' });
    setModal(true);
  }
  function abrirEditar(s: SitTrib) {
    setEditando(s);
    setForm({ codigo: s.codigo, tipo: s.tipo, descricao: s.descricao, aliquota: s.aliquota ?? '' });
    setModal(true);
  }

  async function salvar() {
    try {
      const payload = { ...form, aliquota: form.aliquota || null };
      if (editando) {
        await api.put(`/api/situacoes-tributarias/${editando.id}`, payload);
        toast.sucesso('atualizada');
      } else {
        await api.post('/api/situacoes-tributarias', payload);
        toast.sucesso('criada');
      }
      setModal(false);
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
  }

  async function remover() {
    if (!confirmar) return;
    try {
      await api.delete(`/api/situacoes-tributarias/${confirmar.id}`);
      toast.sucesso('inativada');
      setConfirmar(null);
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
  }

  const sticky = useStickyHeader();

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">Situacoes Tributarias</h2>
          <div className="pagina-acoes">
            <input type="search" placeholder="Buscar..." className="busca" value={busca} onChange={(e) => setBusca(e.target.value)} />
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={mostrarInativas} onChange={(e) => setMostrarInativas(e.target.checked)} />
              Mostrar inativas
            </label>
            <button type="button" className="btn-primario" onClick={abrirNovo}>+ Nova</button>
          </div>
        </div>
      </div>

      {carregando ? <p className="muted">Carregando...</p> : filtradas.length === 0 ? <p className="muted">Nenhuma.</p> : (
        <table className="tabela" style={sticky.tableStyle}>
          <thead><tr>
            <th style={sticky.thStyle}>Codigo</th>
            <th style={sticky.thStyle}>Tipo</th>
            <th style={sticky.thStyle}>Descricao</th>
            <th style={{ ...sticky.thStyle, textAlign: 'right' }}>Aliquota</th>
            <th style={sticky.thStyle}>Status</th>
            <th style={{ ...sticky.thStyle, textAlign: 'right' }}>Acoes</th>
          </tr></thead>
          <tbody>
            {filtradas.map((s) => {
              const acoes: AcaoLinha[] = [
                { label: 'Editar', icon: Pencil, onClick: () => abrirEditar(s) },
                { label: 'Inativar', icon: Ban, perigoso: true, oculto: s.status !== 'ativa', onClick: () => setConfirmar(s) },
              ];
              return (
                <tr key={s.id} onDoubleClick={() => abrirEditar(s)} style={{ cursor: 'pointer' }}>
                  <td>{s.codigo}</td>
                  <td>{s.tipo}</td>
                  <td>{s.descricao}</td>
                  <td style={{ textAlign: 'right' }}>{s.aliquota ? `${s.aliquota}%` : '—'}</td>
                  <td><StatusBadge ativo={s.status === 'ativa'} labelAtivo="Ativa" labelInativo="Inativa" /></td>
                  <td style={{ textAlign: 'right' }}>
                    <MenuAcoesLinha acoes={acoes} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Modal aberto={modal} onFechar={() => setModal(false)} titulo={editando ? 'Editar' : 'Nova'} largura={520}
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={() => setModal(false)}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={salvar} disabled={!form.codigo || !form.descricao}>Salvar</button>
          </>
        }
      >
        <div className="form-grid">
          <label>
            Codigo
            <UpperInput value={form.codigo} onChange={(v) => setForm({ ...form, codigo: v })} maxLength={10} autoFocus />
          </label>
          <label>
            Tipo
            <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
              <option value="ICMS">ICMS</option>
              <option value="IPI">IPI</option>
              <option value="PIS">PIS</option>
              <option value="COFINS">COFINS</option>
            </select>
          </label>
          <label className="full">
            Descricao
            <UpperInput value={form.descricao} onChange={(v) => setForm({ ...form, descricao: v })} maxLength={100} />
          </label>
          <label>
            Aliquota (%)
            <input type="number" step="0.01" value={form.aliquota} onChange={(e) => setForm({ ...form, aliquota: e.target.value })} />
          </label>
        </div>
      </Modal>

      <ConfirmModal aberto={!!confirmar} titulo="Inativar" mensagem={`Inativar "${confirmar?.descricao}"?`} textoConfirmar="Inativar" perigoso onConfirmar={remover} onCancelar={() => setConfirmar(null)} />
    </div>
  );
}
